import React, {useState, useEffect} from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import PageTitle from './PageTitle';
import ErrorDisplay from './ErrorDisplay';
import Waiting from './Waiting';

import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';

import clsx from 'clsx';
import useStyles from '../styles/Styles'

import _ from 'lodash';
import * as yup from 'yup';

import { makeRequest, resetErrorMessage } from '../actions';

const RequestResetPassword = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	
	const memberLabels = useSelector(state => state.settings.labels_members.data);
	const email = useSelector(state => state.requests.email_password_reset);
	const reset = useSelector(state => state.requests.password_reset_with_code);

	const [step, setStep] = useState('request');
	const [showPassword, setShowPassword] = useState(false);

	const [submitted, setSubmitted] = useState({
		request: false,
		reset: false,
	});
	
	useEffect(() => {
		if(email.submitting){
			setSubmitted({
				request: true,
				reset: false,
			});
		} 
	}, [email])

	// step is not a dependency: we don't want to run this every time step changes.
	useEffect(() => {
		if(submitted.request && email.sent && step === 'request'){
			setStep('reset');
		} // eslint-disable-next-line
	}, [email, submitted])
	
	// submitted is not a dependency: We only want to run this when reset changes
	useEffect(() => {
		if(reset.submitting){
			setSubmitted({...submitted, reset: true})
		} // eslint-disable-next-line
	}, [reset])
	
	const [requestData, setRequestData] = useState({
		member_no: '',
		surname: ''
	});
	
	const [resetData, setResetData] = useState({
		member_no: '',
		temp_code: '',
		password: '',
		confirm_password: '',
	});
	
	const resetForm = () => {
		if(step === 'request'){
			setSubmitted({...submitted, request: false});
			//setRequestData({ member_no: '', surname: '' });
		} else {
			setSubmitted({...submitted, reset: false});
			//setResetData({...resetData, temp_code: '', password: '', confirm_password: ''});
		}
		dispatch(resetErrorMessage());
	}
	
	const startAgain = () => {
		setStep('request');
		setSubmitted({ request: false, reset: false })
	}
	
	const requestSchema = yup.object().shape({
		member_no: yup.string().matches(/^[0-9]+$/, { message: 'Please enter your member number'}),
		surname: yup.string().required('Please enter your surname'),
	});
	
	const resetSchema = yup.object().shape({
		member_no: yup.string().matches(/^[0-9]+$/, { message: 'Please enter your member number'}),
		temp_code: yup.string().required('Please enter the authorisation code that was sent to your email address'),
		password: yup.string().matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?!.*[<>\\&]).{8,20}$/, {message: 'Password must be 8-20 characters long, must contain a mix of uppercase and lowercase letters and numbers, and must not include any of these characters: <>\\&'}),
		confirm_password: yup.string().oneOf([resetData.password],'Password does not match').required('This is a required field'),
	});
	
	const [currentSchema, setCurrentSchema] = useState(requestSchema);
	
	// requestSchema and resetSchema aren't dependencies; only step and resetData
	useEffect(() => {
		if(step === 'request'){
			setCurrentSchema(requestSchema);
		} else {
			setCurrentSchema(resetSchema);
		} // eslint-disable-next-line
	}, [step, resetData]);
	
	const [validator, setValidator] = useState({
		request: {
			member_no: { error: false, message: '' }, 
			surname: { error: false, message: '' }, 
		},
		reset: {
			member_no: { error: false, message: '' }, 
			temp_code: { error: false, message: '' }, 
			password: { error: false, message: '' }, 
			confirm_password: { error: false, message: '' }, 
		}
	});
	
	const storeAndValidate = (step, key, value) => {
		switch(step){
			case 'request':
				setRequestData({...requestData, [key]: value});
				break;
			default:
				setResetData({...resetData, [key]: value});
		}
		
		const copyV = _.cloneDeep(validator);
		
		currentSchema.validateAt(key, {[key]: value})
		.then(val => {
			copyV[step][key] = {error: false, message: ''};
			//setValidator({...validator, [key]: {error: false, message: ''}})
		})
		.catch(err => {
			copyV[step][key] = {error: true, message: err.message};
			//setValidator({...validator, [key]: {error: true, message: err.message}})
		})
		.finally(() => {
			setValidator(copyV);
		})
	}
	
	const fields = {
		request: [
			{
				id: "member_no",
				atts: {
					label: "Member number",
					autoComplete: "off",
					value: requestData.member_no
				}
			},
			{
				id: "surname",
				atts: {
					label: memberLabels.surname,
					autoComplete: "surname",
					value: requestData.surname
				}
			},
		],
		reset: [
			{
				id: "member_no",
				atts: {
					label: "Member number",
					autoComplete: "off",
					value: resetData.member_no
				}
			},
			{
				id: "temp_code",
				atts: {
					label: "Authorisation code",
					autoComplete: "off",
					value: resetData.temp_code
				}
			},
			{
				id: "password",
				atts: {
					label: "New password",
					type: showPassword ? 'text' : 'password',
					autoComplete: "off",
					value: resetData.password,
					InputProps: {
						endAdornment: (
						  <InputAdornment position="end">
							<IconButton
							  aria-label="toggle password visibility"
							  onClick={() => setShowPassword(!showPassword)}
							  onMouseDown={(event) => event.preventDefault()}
							  edge="end"
							>
							  {showPassword ? <Visibility /> : <VisibilityOff />}
							</IconButton>
						  </InputAdornment>
						)
					}
				}
			},
			{
				id: "confirm_password",
				atts: {
					label: "Confirm new password",
					type: showPassword ? 'text' : 'password',
					autoComplete: "off",
					value: resetData.confirm_password,
					InputProps: {
						endAdornment: (
						  <InputAdornment position="end">
							<IconButton
							  aria-label="toggle password visibility"
							  onClick={() => setShowPassword(!showPassword)}
							  onMouseDown={(event) => event.preventDefault()}
							  edge="end"
							>
							  {showPassword ? <Visibility /> : <VisibilityOff />}
							</IconButton>
						  </InputAdornment>
						)
					}
				}
			},
		]
	};
	
	const maybeSubmit = () => {
		const d = step === 'request' ? requestData : resetData;

		currentSchema.validate(d, {abortEarly: false})
		.then((value) => {
			if(step === 'request'){
				dispatch(makeRequest('password_reset_email', requestData));
				setResetData({...resetData, member_no: requestData.member_no});
			} else {
				dispatch(makeRequest('password_reset_code', resetData));
			}
		})
		.catch((err) => {
			const copyV = _.cloneDeep(validator);
			_.each(err.inner, (e) => {
				copyV[step][e.path].error = true;
				copyV[step][e.path].message = e.message;
			});
			setValidator(copyV);
		});
	}
	

	
	const requestContent = () => {
		if(!email.submitting && !submitted.request){
			return <>
			<Typography variant="body2" gutterBottom>
			Fill out and submit this form in order to receive a password reset authorisation code by email.
			</Typography>
			<Typography paragraph>
			If you already have an authorisation code, you can <Button color='primary' size='small' variant='outlined'
				onClick={e => setStep('reset')}
			>enter it here</Button>.
			</Typography>
			<form className={classes.form} autoComplete="off" noValidate> 
			<Grid container spacing={2}>
			{fields.request.map((f, i) => (
				<Grid item xs={12} key={i} >
				<TextField 
					variant="outlined"
					required
					fullWidth
					error={validator.request[f.id].error}
					helperText={validator.request[f.id].message}
					onChange={(event) => storeAndValidate(step, f.id, event.target.value )}
					{...f.atts}
					/>
				</Grid>
			))}
				<Grid item xs={12}>
				<Button
					type="button"
					fullWidth
					variant="contained"
					color="primary"
					className={classes.submit}
					onClick={() => maybeSubmit()}
				  >Submit
				  </Button>
				</Grid>
			</Grid>
			</form>
			</>
		} else if(email.submitting){
			return <Waiting
				message='Submitting your request. Please wait...'
				/>
		} else if(submitted.request && email.sent){
			return <>
			<Typography variant='h6' component='h3' gutterBottom>Email sent.</Typography>
			<Typography paragraph>An email containing a password reset authorisation code has been sent to the email address on file for member number {requestData.member_no}. The authorisation code will remain valid for one hour.</Typography>
			</>
		} else if(submitted.request && !email.sent) {
			return <>
			<Typography variant='h6' component='h3' gutterBottom>Email NOT sent.</Typography>
			<Typography paragraph>Failed to send password reset email. Check the error message above.</Typography>
			</>;
		} else {
			return null;
		}
	}
	
	const resetContent = () => {
		if(!reset.submitting && !submitted.reset){
			return <>
			<Typography paragraph>Please enter the authorisation code that was sent to your email address, along with a new password, below. The authorisation code is valid for one hour from the time it was sent: If your code has expired, you can <Button
				color='primary' size='small' variant='outlined'
				onClick={e => startAgain()}
			>request a new code</Button>.</Typography>
			<form className={classes.form} autoComplete="off" noValidate> 
			<Grid container spacing={2}>
			{fields.reset.map((f, i) => (
				<Grid item xs={12} key={i} >
				<TextField 
					variant="outlined"
					required
					fullWidth
					error={validator.reset[f.id].error}
					helperText={validator.reset[f.id].message}
					onChange={(event) => storeAndValidate(step, f.id, event.target.value )}
					{...f.atts}
					/>
				</Grid>
			))}
				<Grid item xs={12}>
				<Button
					type="button"
					fullWidth
					variant="contained"
					color="primary"
					className={classes.submit}
					onClick={() => maybeSubmit()}
				  >Submit
				  </Button>
				</Grid>
			</Grid>
			</form>
			</>
		} else if(reset.submitting){
			return <Waiting
				message='Submitting your request. Please wait...'
				/>
		} else if(submitted.reset && reset.updated){
			return <>
			<Typography variant='h6' component='h3' gutterBottom>Password updated.</Typography>
			<Typography paragraph>Success: Your password has been updated. Would you like to <Link to="/login">login</Link>?</Typography>
			</>
		} else if(submitted.reset && !reset.updated) {
			return <>
			<Typography variant='h6' component='h3' gutterBottom>Password update failed.</Typography>
			<Typography paragraph>Failed to update password. Check the error message above.</Typography>
			</>;
		} else {
			return null;
		}
	}
	
	return (
	<>
	{submitted ?
	<ErrorDisplay
	extra_button={{
			atts: {
				onClick: () => resetForm()
			},
			text: 'Return to form',
		}}
	/> : 
	<ErrorDisplay />
	}
	<Paper className={clsx(classes.innerContent, classes.page)} square={true}>
	<PageTitle title={step === 'request' ? 'Request password reset' : 'Reset password' }/>
	{ step === 'request' ? requestContent() : resetContent() }
	</Paper>
	</>
)};

export default RequestResetPassword